/**
 * Small visual indicator that a phase rotation has been confirmed and applied
 * to the per-phase values displayed alongside it. Two sizes: 'inline' for
 * embedding next to a serial number / TAF header, and 'pill' for the slightly
 * larger badge used above the per-phase table.
 *
 * The mapping displayed matches the backend's PhaseRotationApplier.Permute:
 *   rotation = 1 → reported_L1 ⇒ true L2, reported_L2 ⇒ L3, reported_L3 ⇒ L1
 *   rotation = 2 → reported_L1 ⇒ true L3, reported_L2 ⇒ L1, reported_L3 ⇒ L2
 */
interface Props {
  rotation: number | null | undefined;
  size?: 'inline' | 'pill';
}

function rotationDescription(rotation: number): string {
  if (rotation === 1) return 'L1→L2, L2→L3, L3→L1';
  if (rotation === 2) return 'L1→L3, L2→L1, L3→L2';
  return '';
}

export function PhaseRotationBadge({ rotation, size = 'inline' }: Props) {
  if (!rotation || rotation === 0) return null;
  const desc = rotationDescription(rotation);
  const tooltip = `Phasendreher korrigiert (${desc}). Die unten gezeigten Phasenwerte sind bereits nach Bestätigung im Phasendreher-Tab umgemappt.`;

  if (size === 'pill') {
    return (
      <span
        title={tooltip}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 leading-tight"
      >
        <RotationIcon />
        Phasen korrigiert: {desc}
      </span>
    );
  }

  return (
    <span
      title={tooltip}
      className="inline-flex items-center gap-0.5 px-1 py-0 rounded text-[9px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 leading-tight"
    >
      <RotationIcon />
      r={rotation}
    </span>
  );
}

function RotationIcon() {
  return (
    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v6h6M20 20v-6h-6M4 10a8 8 0 0114-3M20 14a8 8 0 01-14 3" />
    </svg>
  );
}
